import { ResizeSettings } from "store/types/resizeSettings";
import { ImageExtension } from "store/types/imageExtension";
import { Sort } from "store/types/sort";
import { Task } from "@freeconvert/freeconvert-node/dist/types";
import { Progress } from "store/types/Progress";
import { CropSettings } from "@/store/types/crop";
import { FlipSettings } from "@/store/types/flip";
import { RotateSettings } from "@/store/types/rotate";

export interface BulkResizeState {
    exportSettings: ExportSettings;
    sort: Sort | null;
    resizeSettings: Omit<ResizeSettings, "exportSettings">;
    images: BulkResizeImage[];
    lastModified: "width" | "height" | "percentage" | null;
    fromDownloadPage: boolean;
    editImageModal?: {
        imageId: string;
        operation: "crop" | "flip-rotate";
    } | null;
}

export interface PreviewImage {
    initialDimensions: {
        width: number;
        height: number;
    };
    original: ImageProperties;
    preview: ImageProperties;
    title: string;
    format: string;
    createdAt: Date;
}

export type Unit = "MB" | "KB" | "GB" | "B";
export type Size = `${number} ${Unit}`;

export interface ImageProperties {
    width: number;
    height: number;
    size: Size;
    src: string;
}

export interface BulkResizeImage extends PreviewImage {
    id: string;
    importTask?: Task;
    state: BulkResizeImageState;
    importing?: {
        progress: Progress;
    };
    isCorrupted?: boolean;
    crop: CropSettings;
    flip: FlipSettings;
    rotate: RotateSettings;
    operations: BulkResizeEditorOperation[];
}

export interface BulkResizeEditorOperationValue {
    crop: {
        cropBox: CropSettings["cropBox"];
        rotation: number;
    };
    flip: { value: "vertical" | "horizontal"; rotation: number };
    rotate: {
        direction: "clock-wise" | "counter-clock-wise";
        rotation: number;
        flip: {
            horizontal: boolean;
            vertical: boolean;
        };
    };
}

export type BulkResizeEditorOperation =
    | {
          name: "crop";
          value: BulkResizeEditorOperationValue["crop"];
      }
    | {
          name: "flip";
          value: BulkResizeEditorOperationValue["flip"];
      }
    | {
          name: "rotate";
          value: BulkResizeEditorOperationValue["rotate"];
      };

export type BulkResizeImageState = "added" | "imported" | "importing";

interface ExportSettings {
    optimizationLevel: OptimizationLevel;
    maxFileSize: number;
    fileUnit: FileUnit;
    saveAs: ImageExtension;
}

export type FileUnit = (typeof fileUnits)[number];
export const fileUnits = ["KB", "MB"] as const;

export type OptimizationLevel = (typeof optimizationLevels)[number];
export const optimizationLevels = ["best", "good", "average", "custom"] as const;
